import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from './AuthContext'
import { useProfileContext } from './ProfileContext'

import {
  AppContextType,
  Banner,
  ThemeType,
} from '../types/contexts/AppContext'
import { Language } from '../types/language'
import { getFromToken, JwtKeys, RoleType } from '../utils/jwtUtils'

const AppCtx = createContext<AppContextType | null>(null)

export const useApp = (): AppContextType => {
  const ctx = useContext(AppCtx)
  if (!ctx) throw new Error('useApp must be used inside AppProvider')
  return ctx
}

interface AppProviderProps {
  children: ReactNode
  bannerState: [Banner | null, (b: Banner | null) => void]
}

export const AppProvider = ({ children, bannerState }: AppProviderProps) => {
  const { authInfo } = useAuth()
  const { profile } = useProfileContext()
  const { i18n } = useTranslation()
  const roles = useMemo<RoleType[]>(() => {
    if (!authInfo?.accessToken) return []

    const tokenRoles = getFromToken(authInfo.accessToken, JwtKeys.ROLES)
    if (!Array.isArray(tokenRoles)) return []

    return tokenRoles.filter((role): role is RoleType =>
      Object.values(RoleType).includes(role as RoleType)
    )
  }, [authInfo?.accessToken])

  const [theme, setThemeState] = useState<ThemeType>(() => {
    const storedTheme = localStorage.getItem('ph-theme')

    return Object.values(ThemeType).includes(storedTheme as ThemeType)
      ? (storedTheme as ThemeType)
      : ThemeType.LIGHT
  })
  const [lang, setLangState] = useState<Language>(() => {
    const storedLanguage = localStorage.getItem('ph-lang')?.toUpperCase()
    return Object.values(Language).includes(storedLanguage as Language)
      ? (storedLanguage as Language)
      : Language.RO
  })
  const [, setBanner] = bannerState

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('ph-theme', theme)
  }, [theme])

  useEffect(() => {
    i18n.changeLanguage(lang.toLowerCase())
    localStorage.setItem('ph-lang', lang)
  }, [lang, i18n])

  const setTheme = useCallback((theme: ThemeType) => setThemeState(theme), [])
  const setLang = useCallback(
    (language: Language) => setLangState(language),
    []
  )

  useEffect(() => {
    if (!profile) {
      return
    }

    const profileTheme = profile.theme?.toLowerCase() as ThemeType
    const profileLanguage = profile.language?.toUpperCase() as Language

    if (Object.values(ThemeType).includes(profileTheme)) {
      setTheme(profileTheme)
    }

    if (Object.values(Language).includes(profileLanguage)) {
      setLang(profileLanguage)
    }
  }, [profile, setLang, setTheme])

  const showBanner = useCallback(
    (b: Banner) => {
      setBanner(b)
      setTimeout(() => setBanner(null), 3500)
    },
    [setBanner]
  )

  return (
    <AppCtx.Provider
      value={{ theme, setTheme, lang, setLang, roles, showBanner }}
    >
      {children}
    </AppCtx.Provider>
  )
}
